import { Typography } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import CandidateCard from "./CandidateCard";

const CandidatePage = ({
    candidates,
    job_id,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoadingAiBatch,
    aiSentinelRef,
    aiSearchMode,
    onBookmarkChange,
    searchResponse,
    activeTab,
}) => {
    const observerRef = useRef();
    const useAiBatchLoading =
        aiSearchMode &&
        searchResponse?.user_ids &&
        searchResponse.user_ids.length > 0;
    const lastCandidateElementRef = useCallback(
        (node) => {
            if (useAiBatchLoading) return;

            if (isFetchingNextPage) return;
            if (observerRef.current) observerRef.current.disconnect();

            observerRef.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasNextPage) {
                    console.log("Triggering fetchNextPage");
                    fetchNextPage();
                }
            });

            if (node) observerRef.current.observe(node);
        },
        [useAiBatchLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
    );

    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);
    return (
        <div className="space-y-8">
            {candidates?.length > 0 ? (
                <section aria-label="All Candidates">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
                        {candidates.map((candidate, index) => {
                            const isLast = index === candidates.length - 1;
                            return (
                                <div
                                    key={`${candidate.user_id}-${index}`}
                                    ref={
                                        isLast && !useAiBatchLoading
                                            ? lastCandidateElementRef
                                            : undefined
                                    }
                                >
                                    <CandidateCard
                                        candidate={candidate}
                                        job_id={job_id}
                                        onBookmarkChange={onBookmarkChange}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {useAiBatchLoading && <div ref={aiSentinelRef} className="h-1" />}

                    {isLoadingAiBatch && (
                        <div className="flex justify-center py-6">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    )}

                    {/* Loading indicator for normal infinite scroll - only show if NOT using AI batch loading */}
                    {!useAiBatchLoading && isFetchingNextPage && (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    )}
                </section>
            ) : activeTab === "saved" ? (
                <div className="flex flex-col items-center justify-center text-center h-[60vh]">
                    <p>No saved candidates found</p>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-[350px] h-[250px] relative mb-4">
                        <img
                            src="https://du03hetco8xdw.cloudfront.net/recruiter/view_candidates.png"
                            alt="Person searching illustration"
                            className="object-cover"
                        />
                    </div>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            fontSize: "24px",
                            color: "#333333",
                            mb: 1,
                        }}
                    >
                        Your job is still receiving applications!
                    </Typography>
                    <Typography
                        variant="body1"
                        className="text-gray-600 !text-16 text-center text-balance max-w-[50%]"
                        sx={{
                            mb: 3,
                        }}
                    >
                        We make sure to carefully process every profile. We'll get back to
                        you with a ready list of candidates soon...
                    </Typography>
                </div>
            )}
        </div>
    );
};

export default CandidatePage;
